import { Empty, Spin } from "antd";
import React, { Component } from "react";

type Props = {
  loading: boolean;
  data: { [category: string]: { x: number; y: number }[] };
};

// Curated palette: harmonized hues that pop on the dark dashboard surface.
const colors: { [gameName: string]: string } = {
  ZombieVR: "#4f87eb", // Dead Ahead — brand blue
  TronVR: "#a855f7", // Cyber Shock — purple
  ArcheryVR: "#06b6d4", // Arrowsong — cyan
  PVPVR: "#ec4899", // Quantum Arena — pink
  Robbers: "#f59e0b", // Cops vs Robbers — amber
  EspionageExpress: "#10b981", // Espionage Express — emerald
  CookdUp: "#f43f5e", // Cook'd Up — rose
  ScifiEscapeVR: "#fbbf24", // Wayfinders — yellow
  WizardApprenticeVR: "#8b5cf6", // Wizard Academy — violet
  TerminatorVR: "#ef4444", // Terminator Uprising — red
  MonkeyMadness: "#14b8a6", // Monkey Madness — teal
  ArcheryDarkCampaign: "#84cc16", // Arrowsong Dark Campaign — lime
};

const fallbackColor = "#6b7fa8";
const TOP_N = 9;

type Row = {
  key: string;
  name: string;
  color: string;
  values: number[];
};

export default class StackedBarChart extends Component<Props> {
  buildMatrix(): { months: number[]; rows: Row[]; max: number } {
    const { data } = this.props;

    const monthSet = new Set<number>();
    for (const points of Object.values(data)) {
      for (const p of points) {
        if (typeof p.x === "number" && !isNaN(p.x)) monthSet.add(p.x);
      }
    }
    const months = Array.from(monthSet).sort((a, b) => a - b);

    const totals: { [key: string]: number } = {};
    for (const [key, points] of Object.entries(data)) {
      totals[key] = points.reduce((sum, p) => sum + p.y, 0);
    }
    const sortedGames = Object.keys(data)
      .filter((k) => totals[k] > 0)
      .sort((a, b) => totals[b] - totals[a]);
    const topGames = sortedGames.slice(0, TOP_N);
    const otherGames = sortedGames.slice(TOP_N);

    const rows: Row[] = topGames.map((key) => {
      const byMonth: { [m: number]: number } = {};
      for (const p of data[key]) byMonth[p.x] = (byMonth[p.x] || 0) + p.y;
      return {
        key,
        name: getGameName(key),
        color: colors[key] || fallbackColor,
        values: months.map((m) => byMonth[m] || 0),
      };
    });

    if (otherGames.length > 0) {
      const otherValues = months.map((m) => {
        let sum = 0;
        for (const key of otherGames) {
          for (const p of data[key]) {
            if (p.x === m) sum += p.y;
          }
        }
        return sum;
      });
      const hasOtherData = otherValues.some((v) => v > 0);
      if (hasOtherData) {
        rows.push({
          key: "_other",
          name: "Other",
          color: fallbackColor,
          values: otherValues,
        });
      }
    }

    let max = 0;
    for (const row of rows) {
      for (const v of row.values) if (v > max) max = v;
    }

    return { months, rows, max };
  }

  render() {
    if (this.props.loading) return <Spin />;
    if (!this.props.data || !Object.keys(this.props.data).length)
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

    const { months, rows, max } = this.buildMatrix();

    if (!rows.length || !months.length)
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

    return (
      <div className="games-heatmap">
        <div className="games-heatmap-header">
          <div>
            <h3>Plays by Game and Month</h3>
            <p>Number of times each game was played each month.</p>
          </div>
          <div className="games-heatmap-scale">
            <span>0</span>
            <span className="games-heatmap-scale-gradient" />
            <span>Max</span>
          </div>
        </div>
        <div className="games-heatmap-table-wrap">
          <table className="games-heatmap-table">
            <thead>
              <tr>
                <th className="games-heatmap-game-col">Game</th>
                {months.map((m) => (
                  <th key={m}>{formatMonth(m)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <th>
                    <div className="games-heatmap-game-cell">
                      <span
                        className="games-heatmap-dot"
                        style={{ background: row.color }}
                      />
                      <span>{row.name}</span>
                    </div>
                  </th>
                  {row.values.map((v, i) => (
                    <td
                      key={i}
                      className={
                        v === 0
                          ? "games-heatmap-cell games-heatmap-cell--zero"
                          : "games-heatmap-cell"
                      }
                      style={
                        v === 0
                          ? undefined
                          : { background: getHeatColor(v, max) }
                      }
                      title={`${row.name} · ${v} game${
                        v === 1 ? "" : "s"
                      } · ${formatMonth(months[i])}`}
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

// Gradient: dark navy → brand blue → purple → bright pink
const HEAT_STOPS: { t: number; rgb: [number, number, number] }[] = [
  { t: 0, rgb: [40, 50, 80] },
  { t: 0.35, rgb: [79, 135, 235] },
  { t: 0.7, rgb: [168, 85, 247] },
  { t: 1, rgb: [236, 72, 153] },
];

function getHeatColor(value: number, max: number): string {
  if (max <= 0 || value <= 0) return "transparent";
  // sqrt scaling so smaller values aren't drowned out by a few big outliers
  const intensity = Math.min(Math.sqrt(value / max), 1);

  let i = 0;
  while (i < HEAT_STOPS.length - 1 && intensity > HEAT_STOPS[i + 1].t) i++;
  const start = HEAT_STOPS[i];
  const end = HEAT_STOPS[Math.min(i + 1, HEAT_STOPS.length - 1)];
  const span = end.t - start.t || 1;
  const localT = (intensity - start.t) / span;

  const r = Math.round(start.rgb[0] + (end.rgb[0] - start.rgb[0]) * localT);
  const g = Math.round(start.rgb[1] + (end.rgb[1] - start.rgb[1]) * localT);
  const b = Math.round(start.rgb[2] + (end.rgb[2] - start.rgb[2]) * localT);

  const alpha = 0.35 + intensity * 0.55;
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

function formatMonth(timestamp: number) {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "—";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return (
    months[date.getMonth()] + " '" + date.getFullYear().toString().slice(-2)
  );
}

export function getGameName(key: string) {
  const gameNames: { [key: string]: string } = {
    ZombieVR: "Dead Ahead",
    TronVR: "Cyber Shock",
    ArcheryVR: "Arrowsong",
    PVPVR: "Quantum Arena",
    Robbers: "Cops Vs Robbers",
    EspionageExpress: "Espionage Express",
    CookdUp: "Cook'd Up",
    ScifiEscapeVR: "Wayfinders",
    WizardApprenticeVR: "Wizard Academy",
    TerminatorVR: "Terminator Uprising",
    MonkeyMadness: "Monkey Madness",
    ArcheryDarkCampaign: "Arrowsong Dark Campaign",
  };

  return gameNames[key] || key;
}
